
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Phone,
  MapPin,
  X,
  ChevronDown,
  Home,
  Info,
  Car,
  Image as ImageIcon,
  Star,
  Mail,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import "./Header.css";
import logo from "../assets/logo6.png";
import BookingModal from "../components/BookingModal";

const NAV_LINKS = [
  { label: "Home", to: "/", icon: Home },
  { label: "About Us", to: "/about", icon: Info },
  { label: "Our Service", to: "/services", icon: Car },
  { label: "Gallery", to: "/gallery", icon: ImageIcon },
];

const REVIEW_LINK = {
  label: "Review",
  to: "/review",
  icon: Star,
};

const CONTACT_LINK = {
  label: "Contact",
  to: "/contact",
  icon: Mail,
};

const DESTINATION_LINKS = [
  {
    label: "Statue of Unity",
    to: "/destinations/statue-of-unity",
  },
  {
    label: "Pavagadh",
    to: "/destinations/pavagadh",
  },
  {
    label: "Poicha",
    to: "/destinations/poicha",
  },
  {
    label: "Dakor",
    to: "/destinations/dakor",
  },
  {
    label: "Kavi Kamboi Mahadev Mandir",
    to: "/destinations/kavi-kamboi-mahadev-mandir",
  },
];

// Exact route matching with support for nested pages.
// /destinations/pavagadh will never activate /services.
const isRouteActive = (pathname, to) => {
  if (to === "/") {
    return pathname === "/";
  }

  return pathname === to || pathname.startsWith(`${to}/`);
};

export default function Header({
  brandName = "Bhole Cab Service",
  phone = "+917574098964",
  address = "Vadodara, Gujarat 390025",
}) {
  const [scrolled, setScrolled] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Both dropdowns start closed.
  const [destinationsOpen, setDestinationsOpen] = useState(false);
  const [mobileDestinationsOpen, setMobileDestinationsOpen] =
    useState(false);

  const location = useLocation();
  const pathname = location.pathname;
  const cleanPhone = phone.replace(/[^\d+]/g, "");

  // Highlight Destinations only on a destination page.
  const destinationActive = pathname.startsWith("/destinations/");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Close menus whenever the page changes.
  useEffect(() => {
    setSidebarOpen(false);
    setDestinationsOpen(false);
    setMobileDestinationsOpen(false);
  }, [pathname]);

  // Prevent background scrolling when the sidebar or modal is open.
  useEffect(() => {
    document.body.style.overflow =
      sidebarOpen || showPopup ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen, showPopup]);

  // Escape closes the sidebar and dropdowns.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSidebarOpen(false);
        setDestinationsOpen(false);
        setMobileDestinationsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleBookNow = () => {
    setSidebarOpen(false);
    setDestinationsOpen(false);
    setMobileDestinationsOpen(false);
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen((open) => {
      const nextOpen = !open;

      if (nextOpen) {
        setDestinationsOpen(false);
        setMobileDestinationsOpen(false);
      }

      return nextOpen;
    });
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setMobileDestinationsOpen(false);
  };

  // Main navigation links.
  const renderNavLink = (link, mobile = false) => {
    const Icon = link.icon;

    // Each link is evaluated independently.
    const active = isRouteActive(pathname, link.to);

    return (
      <Link
        key={link.to}
        to={link.to}
        className={[
          mobile ? "bhh-sidebar-link" : "bhh-nav-link",
          active ? "bhh-active" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={mobile ? closeSidebar : undefined}
        aria-current={active ? "page" : undefined}
      >
        {mobile && <Icon size={19} aria-hidden="true" />}
        <span>{link.label}</span>
      </Link>
    );
  };

  // Destination links for desktop and mobile.
  const renderDestinationLinks = (mobile = false) =>
    DESTINATION_LINKS.map((item) => {
      const active = isRouteActive(pathname, item.to);

      return (
        <Link
          key={item.to}
          to={item.to}
          className={[
            "bhh-destination-link",
            active ? "bhh-active" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => {
            setDestinationsOpen(false);
            setMobileDestinationsOpen(false);

            if (mobile) {
              closeSidebar();
            }
          }}
          aria-current={active ? "page" : undefined}
        >
          {item.label}
        </Link>
      );
    });

  return (
    <>
      <header
        className={`bhh-site-header ${
          scrolled ? "bhh-is-scrolled" : ""
        }`}
      >
        <div className="bhh-navbar">
          <div className="bhh-navbar-inner">
            {/* Brand */}
            <Link
              to="/"
              className="bhh-brand"
              aria-label={`${brandName} home`}
            >
              <img
                src={logo}
                alt={brandName}
                className="bhh-brand-logo"
              />
            </Link>

            {/* Desktop navigation */}
            <nav
              className="bhh-nav-links"
              aria-label="Main navigation"
            >
              {NAV_LINKS.map((link) => renderNavLink(link))}

              {/* Desktop destinations dropdown */}
              <div
                className={`bhh-destination-nav ${
                  destinationActive ? "bhh-active" : ""
                }`}
                onMouseEnter={() => setDestinationsOpen(true)}
                onMouseLeave={() => setDestinationsOpen(false)}
              >
                <button
                  type="button"
                  className={`bhh-destination-trigger ${
                    destinationActive ? "bhh-active" : ""
                  }`}
                  onClick={() =>
                    setDestinationsOpen((open) => !open)
                  }
                  aria-expanded={destinationsOpen}
                  aria-haspopup="true"
                >
                  <span>Destinations</span>

                  <ChevronDown
                    size={16}
                    className={
                      destinationsOpen ? "bhh-rotated" : ""
                    }
                  />
                </button>

                <div
                  className={`bhh-destination-menu ${
                    destinationsOpen ? "bhh-is-open" : ""
                  }`}
                >
                  {renderDestinationLinks()}
                </div>
              </div>

              {renderNavLink(REVIEW_LINK)}
              {renderNavLink(CONTACT_LINK)}
            </nav>

            {/* Desktop booking button */}
            <button
              type="button"
              onClick={handleBookNow}
              className="bhh-btn-book bhh-desktop-book"
            >
              Book Now
            </button>

            {/* Mobile hamburger */}
            <button
              type="button"
              className={`bhh-sidebar-toggle ${
                sidebarOpen ? "bhh-is-open" : ""
              }`}
              aria-label={
                sidebarOpen
                  ? "Close navigation menu"
                  : "Open navigation menu"
              }
              aria-expanded={sidebarOpen}
              aria-controls="bh-mobile-sidebar"
              onClick={toggleSidebar}
            >
              <span
                className="bhh-hamburger-lines"
                aria-hidden="true"
              >
                <i />
                <i />
                <i />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      <button
        type="button"
        className={`bhh-sidebar-overlay ${
          sidebarOpen ? "bhh-active" : ""
        }`}
        aria-label="Close navigation menu"
        onClick={closeSidebar}
        tabIndex={sidebarOpen ? 0 : -1}
      />

      {/* Mobile sidebar */}
      <aside
        id="bh-mobile-sidebar"
        className={`bhh-sidebar ${
          sidebarOpen ? "bhh-active" : ""
        }`}
        aria-hidden={!sidebarOpen}
        inert={!sidebarOpen}
      >
        {/* Sidebar header */}
        <div className="bhh-sidebar-header">
          <Link
            to="/"
            className="bhh-sidebar-brand"
            onClick={closeSidebar}
          >
            <img
              src={logo}
              alt={brandName}
              className="bhh-sidebar-logo"
            />
          </Link>

          <button
            type="button"
            className="bhh-sidebar-close"
            onClick={closeSidebar}
            aria-label="Close navigation menu"
          >
            <X size={23} />
          </button>
        </div>

        {/* Sidebar content */}
        <div className="bhh-sidebar-body">
          <nav
            className="bhh-sidebar-nav"
            aria-label="Mobile navigation"
          >
            {NAV_LINKS.map((link) => renderNavLink(link, true))}

            {/* Mobile destinations dropdown */}
            <div className="bhh-sidebar-destinations">
              <button
                type="button"
                className={`bhh-sidebar-link bhh-sidebar-destination-trigger ${
                  destinationActive ? "bhh-active" : ""
                }`}
                onClick={() =>
                  setMobileDestinationsOpen((open) => !open)
                }
                aria-expanded={mobileDestinationsOpen}
              >
                <MapPin size={19} aria-hidden="true" />

                <span>Destinations</span>

                <ChevronDown
                  size={16}
                  className={
                    mobileDestinationsOpen ? "bhh-rotated" : ""
                  }
                />
              </button>

              {mobileDestinationsOpen && (
                <div className="bhh-sidebar-destination-list">
                  {renderDestinationLinks(true)}
                </div>
              )}
            </div>

            {renderNavLink(REVIEW_LINK, true)}
            {renderNavLink(CONTACT_LINK, true)}
          </nav>

          <div className="bhh-sidebar-divider" />

          {/* Contact information */}
          <div className="bhh-sidebar-contact">
            <h4>Contact Info</h4>

            <a
              href={`tel:${cleanPhone}`}
              className="bhh-sidebar-contact-item"
            >
              <Phone size={18} />
              <span>{phone}</span>
            </a>

            <a
              href="mailto:info@bholecabservices.com"
              className="bhh-sidebar-contact-item"
            >
              <Mail size={18} />
              <span>info@bholecabservices.com</span>
            </a>

            <div className="bhh-sidebar-contact-item">
              <MapPin size={18} />
              <span>{address}</span>
            </div>
          </div>

          {/* Booking button */}
          <button
            type="button"
            onClick={handleBookNow}
            className="bhh-sidebar-book-btn"
          >
            <FaWhatsapp
              className="bhh-sidebar-whatsapp-icon"
              aria-hidden="true"
            />
            Book Now
          </button>

          <div className="bhh-sidebar-footer">
            <p>
              © {new Date().getFullYear()} {brandName}
            </p>
          </div>
        </div>
      </aside>

      {/* Booking modal */}
      <BookingModal
        isOpen={showPopup}
        onClose={closePopup}
        phone={phone}
      />
    </>
  );
}
